import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { handleUnauthorizedResponse } from '@/auth/sessionExpiry';

/**
 * Centralized 401 handler for every query/mutation. Individual calls no longer
 * need to handle session expiry themselves — any 401 that surfaces here routes
 * through the session-expired flow (silent renew, else the global modal).
 */
const handleQueryError = (error: unknown): void => {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 401) {
    void handleUnauthorizedResponse();
  }
};

/**
 * TanStack Query client configuration
 */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleQueryError }),
  mutationCache: new MutationCache({ onError: handleQueryError }),
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (was cacheTime)
      retry: (failureCount, error) => {
        // Don't retry on auth errors
        const axiosError = error as { response?: { status?: number } };
        if (axiosError?.response?.status === 401 || axiosError?.response?.status === 403) {
          return false;
        }
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});
